import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { PulseLoader } from "react-spinners"
import { runInAction } from "mobx"
import { observer, useLocalObservable } from "mobx-react-lite"
import { BackIcon, BorderButton, ClockIcon, Header, Image, Line, LocationIcon, ScreenContainer, StyledButton, Text, Title } from "../components"
import { FIRESTORE_COLLECTIONS } from "../enums"
import { IEventCollectionItem } from "../interfaces"
import Firestore from "../services/FirestoreService"
import AuthStore from "../stores/AuthStore"
import { formatDate } from "../utils"

export const EventDetailsPage = observer(() => {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()

	const store = useLocalObservable(() => ({
		isLoading: false,
		isButtonLoading: false,
		error: null as Error | null,
		event: null as IEventCollectionItem | null,

		get userId(): string | null {
			return AuthStore.user?.userId ?? null
		},

		get isSubscribed() {
			if (!this.userId) return false
			return this.event?.subscribed.includes(this.userId)
		},

		async load(id: string): Promise<void> {
			this.isLoading = true
			this.error = null

			let event: IEventCollectionItem | null = null
			let error: Error | null = null

			try {
				event = await Firestore.get(FIRESTORE_COLLECTIONS.EVENTS_COLLECTION, id)
			} catch (err) {
				error = err as Error
			}

			runInAction(() => {
				this.event = event
				this.error = error
				this.isLoading = false
			})
		},

		async setSubscribed(value: boolean) {
			if (!this.userId) {
				throw new Error("AuthStore user is not initialized")
			}
			if (!this.event) {
				throw new Error("Event not loaded")
			}

			this.isButtonLoading = true
			this.error = null

			let error: Error | null = null

			try {
				const parameters = [FIRESTORE_COLLECTIONS.EVENTS_COLLECTION, this.event._id, "subscribed", this.userId] as const

				if (value) {
					await Firestore.pushToArray(...parameters)
				} else {
					await Firestore.removeFromArray(...parameters)
				}
			} catch (err) {
				error = err as Error
			}

			runInAction(() => {
				this.isButtonLoading = false
				this.error = error
			})

			this.load(this.event._id)
		},
	}))

	useEffect(() => {
		if (id) {
			store.load(id)
		}
	}, [store, id])

	return (
		<ScreenContainer>
			<div className="relative flex h-full flex-col p-[20px] pt-[5px]">
				{store.event ? (
					<>
						<div className="relative mt-[5px]">
							<Image url={store.event.heroImage} width={300} height={300} />

							{/* Navigation header */}
							<div className="absolute top-[5px]">
								<Header
									left={
										<StyledButton onClick={() => navigate("/")} style="flex flex-row items-center">
											<BackIcon />
											<Text style="ml-[5px] ">{t("profile.back")}</Text>
										</StyledButton>
									}
								/>
							</div>

							<div className="absolute inset-x-0 bottom-0 top-[150px] z-0 bg-gradient-to-t from-black"></div>

							{/* Image overlay text */}
							<div className="absolute bottom-0">
								<Title variant="h1">{store.event.name}</Title>
							</div>
						</div>

						<div className={`mt-0 flex flex-row p-[2px] ${store.event.location ? "justify-around" : "justify-start"}`}>
							<div className="flex flex-row gap-[9px]">
								<ClockIcon />
								<Text>{formatDate(store.event.start)}</Text>
							</div>

							{store.event.location && (
								<div className="flex flex-row gap-[9px]">
									<LocationIcon />
									<Text>{store.event.location.name}</Text>
								</div>
							)}
						</div>

						<div className="pl-[10px]">
							<Line />
						</div>

						<div className="w-full pl-[10px] pr-[15px]">
							<Text style="text-justify">{store.event.description}</Text>
						</div>

						<div className="flex flex-row mt-10 justify-center items-center">
							<BorderButton
								disabled={store.isLoading}
								active={!store.isSubscribed}
								text={store.isSubscribed ? t("eventDetails.wont_attend") : t("eventDetails.will_attend")}
								onClick={() => store.setSubscribed(!store.isSubscribed)}
							/>
						</div>
					</>
				) : (
					<PulseLoader />
				)}
			</div>
		</ScreenContainer>
	)
})
